(function () {
  'use strict';

  const STRATEGY_VERSION = '6.3.0-professional-profile';
  const INTENSITY_RANK = { gentle: 1, moderate: 2, strong: 3, specialist: 4 };

  function uniq(values) { return Array.from(new Set((values || []).filter(Boolean))); }
  function clamp(value, min, max) { return Math.max(min, Math.min(max, Number(value || 0))); }

  function normalizeProfile(input) {
    const p = input || {};
    if (p.__normalizedQuizProfile === true) return p;
    const modifiers = uniq(p.skinTypeModifiers || []).filter(x => ['sensitive', 'mature'].includes(x));
    return {
      skinType: ['dry', 'combination', 'oily', 'normal', 'unsure'].includes(p.skinType) ? p.skinType : 'unsure',
      skinTypeModifiers: modifiers,
      sensitiveSkinType: modifiers.includes('sensitive'),
      matureSkinType: modifiers.includes('mature'),
      primaryConcern: p.primaryConcern || null,
      primaryChoice: p.primaryChoice || p.goalChoice || null,
      secondaryConcerns: uniq(p.secondaryConcerns || []).filter(c => c && c !== p.primaryConcern).slice(0, 2),
      sensitivity: clamp(p.sensitivity || 0, 0, 4),
      barrierState: ['stable', 'sometimes', 'reactive', 'compromised'].includes(p.barrierState) ? p.barrierState : (p.barrierCompromised ? 'compromised' : 'stable'),
      barrierCompromised: Boolean(p.barrierCompromised || p.barrierState === 'compromised'),
      activeExperience: ['beginner', 'intermediate', 'advanced'].includes(p.activeExperience) ? p.activeExperience : 'beginner',
      excludeRetinoids: Boolean(p.excludeRetinoids),
      excludeAcids: Boolean(p.excludeAcids),
      excludeSpicules: Boolean(p.excludeSpicules),
      routineStyle: ['minimal', 'smart', 'ritual'].includes(p.routineStyle) ? p.routineStyle : 'smart',
      makeupFrequency: ['rarely', 'sometimes', 'most_days'].includes(p.makeupFrequency) ? p.makeupFrequency : 'rarely',
      budgetTotal: p.budgetTotal ? Number(p.budgetTotal) : null,
      fragranceFreeOnly: Boolean(p.fragranceFreeOnly),
      veganOnly: Boolean(p.veganOnly),
      crueltyFreeOnly: Boolean(p.crueltyFreeOnly),
      __normalizedQuizProfile: true
    };
  }

  function analyzeSkinState(inputProfile) {
    const p = normalizeProfile(inputProfile);
    let state = 'stable';
    if (p.barrierState === 'compromised' || p.barrierCompromised) state = 'barrier_compromised';
    else if (p.barrierState === 'reactive') state = 'reactive';
    else if (p.barrierState === 'sometimes') state = 'sometimes';

    const hydrationNeed = p.skinType === 'dry' || (p.secondaryConcerns || []).includes('dryness') || state === 'barrier_compromised'
      ? 'elevated' : 'normal';

    // Stable sensitive skin is a trait, not a reactive state.  It shifts safety
    // preference but does not rewrite the current state.
    const sensitivePreference = p.sensitiveSkinType || p.sensitivity >= 2;
    const maxIntensity = state === 'barrier_compromised' || state === 'reactive'
      ? 'gentle'
      : p.activeExperience === 'advanced' ? 'strong' : 'moderate';

    return {
      state,
      hydrationNeed,
      sensitivePreference,
      matureSkin: p.matureSkinType,
      maxIntensity,
      currentBarrierUnstable: state === 'reactive' || state === 'barrier_compromised'
    };
  }

  function primaryStrategy(profile) {
    if (profile.primaryConcern === 'blemishes') return 'blemish_control';
    if (profile.primaryConcern === 'pores') return 'pore_refinement';
    if (profile.primaryConcern === 'pigmentation') return 'pigment_correction';
    if (profile.primaryConcern === 'redness') return 'redness_support';
    if (profile.primaryConcern === 'anti_age' || profile.primaryConcern === 'lines' || profile.primaryConcern === 'firmness') return 'anti_age';
    if (profile.primaryConcern === 'glass_skin') return 'glass_skin';
    if (profile.primaryConcern === 'dryness') return 'hydration';
    return 'hydration';
  }

  function secondaryStrategy(concern) {
    const map = {
      dryness: 'hydration', blemishes: 'blemish_control', pores: 'pore_refinement',
      pigmentation: 'pigment_correction', redness: 'redness_support', dullness: 'glow',
      lines: 'anti_age', firmness: 'anti_age'
    };
    return map[concern] || null;
  }

  function generateStrategy(inputProfile, skinState) {
    const profile = normalizeProfile(inputProfile);
    const state = skinState || analyzeSkinState(profile);
    const primary = primaryStrategy(profile);
    const secondaries = profile.secondaryConcerns.map(c => ({ concern: c, strategy: secondaryStrategy(c) })).filter(x => x.strategy);
    const avoidFamilies = [];

    if (profile.excludeRetinoids || state.state === 'barrier_compromised') avoidFamilies.push('retinoid');
    if (profile.excludeAcids || state.state === 'barrier_compromised') avoidFamilies.push('aha', 'bha', 'pha', 'benzoyl_peroxide');
    if (profile.excludeSpicules || state.state !== 'stable' || profile.activeExperience !== 'advanced') avoidFamilies.push('spicule');

    let name = primary;
    let intensityTarget = 'moderate';
    if (primary === 'glass_skin') name = 'glass_skin_hydration_glow';
    if (primary === 'pore_refinement' && profile.skinType === 'dry') { name = 'gentle_texture_balance'; intensityTarget = 'gentle'; }
    if (primary === 'blemish_control' && profile.skinType === 'dry') { name = 'gentle_blemish_control'; intensityTarget = 'gentle'; }
    if (primary === 'redness_support') {
      name = state.currentBarrierUnstable ? 'gentle_redness_support' : 'redness_correction';
      intensityTarget = state.currentBarrierUnstable ? 'gentle' : 'moderate';
    }
    if (state.state === 'reactive' || state.state === 'barrier_compromised') intensityTarget = 'gentle';

    // Beginner means introduction/complexity caution, not physiologically weak skin.
    // It may use moderate, well-tolerated actives but never requires them.
    const maxIntensity = state.maxIntensity;

    const desiredStrategies = [];
    if (primary === 'glass_skin') {
      desiredStrategies.push({ name: 'hydration', weight: 12, reasons: ['primary_glass_skin'] });
      desiredStrategies.push({ name: 'glow', weight: 10, reasons: ['primary_glass_skin'] });
    } else {
      desiredStrategies.push({ name: primary, weight: 12, reasons: ['primary_goal'] });
    }

    // These are contextual/support signals only.  They never vote against the
    // primary treatment gate; the engine applies them later as refinement.
    const supportNeeds = {
      hydration: state.hydrationNeed === 'elevated' ? 3 : 1,
      barrier: state.state === 'barrier_compromised' ? 4 : state.state === 'reactive' ? 3 : state.state === 'sometimes' ? 2 : 1,
      sensitive: state.sensitivePreference ? 2 : 0,
      mature: profile.matureSkinType ? 1 : 0,
      makeup: profile.makeupFrequency,
      routineStyle: profile.routineStyle
    };

    return {
      name,
      anchorStrategy: state.state === 'barrier_compromised' ? 'barrier_support' : primary,
      originalPrimaryStrategy: primary,
      primaryConcern: profile.primaryConcern,
      desiredStrategies,
      secondaryStrategies: secondaries,
      deferredStrategies: state.state === 'barrier_compromised' ? [{ name: primary, weight: 4, reasons: ['deferred_primary_goal'] }] : [],
      supportNeeds,
      intensityTarget,
      maxIntensity,
      avoidFamilies: uniq(avoidFamilies),
      notes: []
    };
  }

  function buildRoutineArchitecture(inputProfile, strategy, state) {
    const profile = normalizeProfile(inputProfile);
    const budget = Number(profile.budgetTotal || 0);
    const low = Boolean(budget && budget <= 60);
    const medium = Boolean(budget && budget > 60 && budget <= 90);
    const moisturizerPriority = Boolean(state && (state.currentBarrierUnstable || state.hydrationNeed === 'elevated' || profile.skinType === 'dry'));
    const foundationOrder = moisturizerPriority ? ['moisturizer', 'cleanser'] : ['cleanser', 'moisturizer'];
    const required = ['active_anchor', ...foundationOrder, 'sunscreen'];
    const optional = [];

    let maxProducts = 4;
    if (profile.routineStyle === 'smart') {
      if (profile.makeupFrequency === 'sometimes' || profile.makeupFrequency === 'most_days') optional.push('oil_cleanser');
      maxProducts = optional.length ? 5 : 4;
    } else if (profile.routineStyle === 'ritual') {
      if (profile.makeupFrequency === 'sometimes' || profile.makeupFrequency === 'most_days') optional.push('oil_cleanser');
      optional.push('support');
      maxProducts = optional.includes('oil_cleanser') ? 6 : 5;
    }

    // Low budget never removes one of the four core functions.  It changes the
    // feasible product set, not the definition of a complete routine.
    return {
      budget: budget || null,
      lowBudget: low,
      mediumBudget: medium,
      premiumMode: false,
      required: uniq(required),
      optional: uniq(optional),
      foundationOrder,
      maxProducts,
      anchorFormats: ['serum', 'ampoule'],
      supportAllowed: profile.routineStyle === 'ritual' && !low && !medium,
      moisturizerPriority
    };
  }

  window.SkinQuizStrategy = {
    normalizeProfile,
    analyzeSkinState,
    generateStrategy,
    buildRoutineArchitecture,
    constants: { STRATEGY_VERSION, INTENSITY_RANK }
  };
})();
